import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import BrickTitle from "@/components/landing/BrickTitle";
import LegoConfetti from "@/components/landing/LegoConfetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trades = [
  "General Multi-Repair Checklist",
  "HVAC / AC EPA Service",
  "Plumbing & Leak Fix",
  "Roof / Gutter Repair",
  "Welding & Metal Fabrication",
  "Custom Woodworking",
  "3D Print Obsolete Part",
];

const initial = { name: "", phone: "", trade: trades[0], area: "", description: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [burst, setBurst] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      setBurst(true);
      setForm(initial);
      toast.success("Quote request received. Expect an honest estimate within 2 business hours.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-lg bg-[#1E252B] border border-[#485663] text-white placeholder-[#737F8A] text-sm focus:outline-none focus:border-[#D4691E] transition-colors";

  return (
    <section id="contact" className="py-20 bg-[#1E252B] text-white" data-testid="contact-section">
      {burst && <LegoConfetti onDone={() => setBurst(false)} />}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-xl mx-auto mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#D4691E]/20 text-[#FFAE70] text-xs font-bold uppercase tracking-wider mb-3">
            Fast Response Guarantee
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold"><BrickTitle text="Request Your Upfront Quote" /></h2>
          <p className="text-sm text-[#A39B92] mt-3">
            Tell us what needs fixing or building. We review the details and provide an honest estimate without pushy
            sales tactics.
          </p>
        </div>

        <div className="bg-[#28323A] p-8 sm:p-10 rounded-2xl border border-[#3A4752] shadow-xl">
          {submitted ? (
            <div className="text-center py-10" data-testid="quote-success-message">
              <h3 className="font-display text-2xl font-bold text-white">Request Received</h3>
              <p className="text-sm text-[#A39B92] mt-3 max-w-md mx-auto">
                Thank you — your job details are in. You'll get an honest, upfront estimate within 2 business hours,
                with the free 10-Point Home Health Inspection bonus applied to qualifying jobs.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="quote-another-btn"
                className="mt-6 px-6 py-3 rounded-lg bg-[#D4691E] hover:bg-[#A94E10] text-white font-bold text-sm transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-[#D8D2CA] mb-2" htmlFor="quote-name">
                    Your Name *
                  </label>
                  <input
                    id="quote-name"
                    data-testid="quote-name-input"
                    type="text"
                    placeholder="e.g. Sarah Jenkins"
                    className={inputCls}
                    value={form.name}
                    onChange={set("name")}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#D8D2CA] mb-2" htmlFor="quote-phone">
                    Phone Number *
                  </label>
                  <input
                    id="quote-phone"
                    data-testid="quote-phone-input"
                    type="tel"
                    placeholder="e.g. (555) 000-0000"
                    className={inputCls}
                    value={form.phone}
                    onChange={set("phone")}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-[#D8D2CA] mb-2" htmlFor="quote-trade">
                    Primary Trade Needed
                  </label>
                  <select
                    id="quote-trade"
                    data-testid="quote-trade-select"
                    className={inputCls}
                    value={form.trade}
                    onChange={set("trade")}
                  >
                    {trades.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#D8D2CA] mb-2" htmlFor="quote-area">
                    Neighborhood / Area
                  </label>
                  <input
                    id="quote-area"
                    data-testid="quote-area-input"
                    type="text"
                    placeholder="e.g. Westside / County Area"
                    className={inputCls}
                    value={form.area}
                    onChange={set("area")}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#D8D2CA] mb-2" htmlFor="quote-description">
                  Describe What You Need Fixed or Built
                </label>
                <textarea
                  id="quote-description"
                  data-testid="quote-description-input"
                  rows="4"
                  placeholder="Describe the task, dimensions, symptoms of leak, or list of honey-do items..."
                  className={inputCls}
                  value={form.description}
                  onChange={set("description")}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-btn"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-[#D4691E] hover:bg-[#A94E10] disabled:opacity-60 text-white font-bold text-sm transition-colors shadow-lg shadow-[#D4691E]/20"
                >
                  {submitting ? "Sending..." : "Submit Quote Request"}
                </button>
                <span className="text-xs text-[#8F9BA6]">We never share your contact information</span>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-[#3A4752] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A39B92]">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#FFAE70]" />
              <span>
                Prefer to call directly? Tap:{" "}
                <a href="tel:+12258927777" data-testid="contact-call-link" className="text-white font-bold hover:text-[#FFAE70] transition-colors">
                  (225) 892-7777
                </a>
              </span>
            </div>
            <span className="text-emerald-400 font-semibold">
              &bull; 10-Point Health Check Bonus automatically applied
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
